import type { ResidueEntry } from "./order33State.ts";
/** ORDER 25 Session 2 — app-side candle rung and cursor-page contract. */

import { rungKey, rungLabel, rungSpanMs, type Rung } from "./rungLadder.ts";
import type { VenueStateLike } from "./session.ts";

const LEFT_EDGE_PREFETCH_BARS = 3;

export interface PagedCandle {
  readonly t: number;
  readonly o: number;
  readonly h: number;
  readonly l: number;
  readonly c: number;
  readonly v: number | null;
}

export interface CandlePage {
  readonly declaredResidue: readonly ResidueEntry[];
  readonly bars: readonly PagedCandle[];
  readonly root: string;
  readonly servedKey: string;
  readonly sessionVerdict: string | null;
  readonly venueState: VenueStateLike | null;
  readonly nextOpen: string | null;
  readonly dataState: string | null;
  readonly sessionWindow: { readonly from: number; readonly to: number } | null;
  readonly ageS: number | null;
  readonly budgetS: number | null;
  readonly hasMore: boolean;
  readonly nextBefore: number | null;
}

export type CandlePageRead =
  | { readonly page: CandlePage; readonly blocked: null }
  | { readonly page: null; readonly blocked: string };

const wireTf = (rung: Rung): string => rung.kind === "submin" ? rung.span : rung.tf;

export function canPageDeepCandles(rung: Rung): boolean {
  return rung.kind === "tf" && Number(rung.tf) >= 3;
}

/** ORDER 48: native 1m pages through its bounded range door; coarse rungs retain `before=`. */
export function canPageAlignedCandles(rung: Rung): boolean {
  return rung.kind === "tf";
}

export function candleRungUrl(
  rung: Rung,
  limit: number,
  symbol = "ES1!",
  before: number | null = null,
): string {
  if (!Number.isFinite(limit)) throw new RangeError(`candleRungUrl: limit must be finite, got ${limit}`);
  const appliedLimit = Math.max(1, Math.min(4_320, Math.floor(limit)));
  if (before !== null && !canPageDeepCandles(rung)) {
    throw new RangeError("before pagination requires an ES timeframe >=3m");
  }
  if (before !== null && (!Number.isSafeInteger(before) || before < 946_684_800_000)) {
    throw new RangeError(`before must be an integer epoch-ms cursor, got ${before}`);
  }
  const query = new URLSearchParams({ symbol, tf: wireTf(rung), limit: String(appliedLimit) });
  if (before !== null) query.set("before", String(before));
  return `/api/es/candles?${query.toString()}`;
}

/**
 * ORDER 52 — one bounded price-only range used to complete a selected VWAP curve's anchor.
 * It never mutates the price/CVD aligned window; the caller holds these bars as curve input only.
 */
export function vwapCurveCoverageUrls(
  rung: Rung,
  anchor: number,
  oldestLoaded: number,
  symbol = "ES1!",
): string[] {
  if (rung.kind !== "tf" || rung.tf !== "1") {
    throw new RangeError("VWAP curve coverage requires native 1m candles so no aggregate bucket straddles its anchor");
  }
  if (![anchor, oldestLoaded].every(Number.isSafeInteger)) {
    throw new RangeError(`VWAP curve coverage requires integer epoch-ms bounds, got ${anchor}..${oldestLoaded}`);
  }
  if (oldestLoaded <= anchor) return [];
  const span = rungSpanMs(rung);
  const urls: string[] = [];
  for (let from = anchor; from < oldestLoaded;) {
    const to = Math.min(oldestLoaded, from + 4_320 * span);
    const bars = Math.ceil((to - from) / span);
    const query = new URLSearchParams({
      symbol,
      tf: wireTf(rung),
      limit: String(Math.max(1, bars)),
      from: String(from),
      to: String(to),
    });
    urls.push(`/api/es/candles?${query.toString()}`);
    from = to;
  }
  return urls;
}

/** One half-open, cover-checkable prefix for V-split study input; these bars are never chart data. */
export function vsplitWarmupWindow(
  rung: Rung,
  firstBar: number,
  need: number,
  symbol = "ES1!",
): { readonly from: number; readonly to: number; readonly url: string } {
  if (!Number.isSafeInteger(firstBar)) {
    throw new RangeError(`V-split warm-up requires an integer first-bar epoch-ms, got ${firstBar}`);
  }
  if (!Number.isSafeInteger(need) || need < 1 || need > 4_320) {
    throw new RangeError(`V-split warm-up need must be an integer within the candle door bound, got ${need}`);
  }
  const to = firstBar;
  const from = to - need * rungSpanMs(rung);
  const query = new URLSearchParams({
    symbol,
    tf: wireTf(rung),
    limit: String(need),
    from: String(from),
    to: String(to),
  });
  return { from, to, url: `/api/es/candles?${query.toString()}` };
}

/** Half-open native-1m coverage may end only at an elapsed minute, never in forming bucket time. */
export function vwapCoverageTargetTo(rung: Rung, latestBar: number, now = Date.now()): number {
  if (![latestBar, now].every(Number.isSafeInteger)) {
    throw new RangeError(`VWAP coverage target requires integer epoch-ms times, got ${latestBar}..${now}`);
  }
  const elapsedMinute = Math.floor(now / 60_000) * 60_000;
  return Math.min(latestBar + rungSpanMs(rung), elapsedMinute);
}

/**
 * One candle page aligned to the CVD page's exclusive cursor. The Worker intentionally refuses
 * `before=` at native 1m, but its existing half-open range door serves the exact same interval.
 */
export function alignedCandleHistoryUrl(
  rung: Rung,
  limit: number,
  before: number,
  symbol = "ES1!",
): string {
  if (!canPageAlignedCandles(rung)) {
    throw new RangeError("aligned candle history requires a minute rung");
  }
  if (rung.kind === "tf" && Number(rung.tf) >= 3) {
    return candleRungUrl(rung, limit, symbol, before);
  }
  if (!Number.isSafeInteger(before) || before < 946_684_800_000) {
    throw new RangeError(`before must be an integer epoch-ms cursor, got ${before}`);
  }
  const appliedLimit = Math.max(1, Math.min(4_320, Math.floor(limit)));
  const from = before - appliedLimit * rungSpanMs(rung);
  const query = new URLSearchParams({
    symbol,
    tf: wireTf(rung),
    limit: String(appliedLimit),
    from: String(from),
    to: String(before),
  });
  return `/api/es/candles?${query.toString()}`;
}

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

function readSessionWindow(value: unknown): CandlePage["sessionWindow"] {
  if (value === null || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  if (typeof raw.open !== "string" || typeof raw.close !== "string") return null;
  const from = Date.parse(raw.open);
  const to = Date.parse(raw.close);
  return Number.isFinite(from) && Number.isFinite(to) && from < to ? { from, to } : null;
}

function readEchoWindow(value: unknown): CandlePage["sessionWindow"] {
  if (value === null || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  return Number.isSafeInteger(raw.from) && Number.isSafeInteger(raw.to)
    && (raw.from as number) < (raw.to as number)
    ? { from: raw.from as number, to: raw.to as number }
    : null;
}

export function readCandlePage(body: unknown, requested: Rung, expectedRoot = "ES"): CandlePageRead {
  if (body === null || typeof body !== "object") {
    return { page: null, blocked: `${rungLabel(requested)} candles returned an unreadable payload` };
  }
  const raw = body as Record<string, unknown>;
  if (raw.root !== expectedRoot) {
    return { page: null, blocked: `candles served root=${String(raw.root)}, expected root=${expectedRoot}; refusing to pool roots` };
  }
  const expectedTf = wireTf(requested);
  if (raw.tf_applied !== expectedTf) {
    return { page: null, blocked: `candles served rung ${String(raw.tf_applied)}, selected ${expectedTf}; refusing a mislabeled series` };
  }
  if (!Array.isArray(raw.bars)) {
    return { page: null, blocked: `${rungLabel(requested)} candles payload has no bars array` };
  }
  const declaredResidue: ResidueEntry[] = [];
  if ("declared_residue" in raw) {
    if (!Array.isArray(raw.declared_residue)) {
      return { page: null, blocked: "candles declared_residue is not an array" };
    }
    for (const [index, value] of raw.declared_residue.entries()) {
      const refuse = (field: string): CandlePageRead => ({ page: null,
        blocked: `candles declared_residue[${index}].${field} is malformed` });
      if (value === null || typeof value !== "object") return refuse("entry");
      const entry = value as Record<string, unknown>;
      if (entry.symbol !== "ES1!" || raw.root !== "ES") return refuse("symbol");
      if (typeof entry.minute !== "string" ||
          !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:00(?:\.000)?Z$/.test(entry.minute) ||
          !Number.isFinite(Date.parse(entry.minute)) ||
          new Date(entry.minute).toISOString() !== entry.minute.replace(":00Z", ":00.000Z")) return refuse("minute");
      if (entry.reason !== "SOURCE_ABSENT") return refuse("reason");
      if (typeof entry.ruling !== "string" || entry.ruling.trim().length === 0) return refuse("ruling");
      if (entry.state !== "ABSENT" && entry.state !== "FILLED") return refuse("state");
      if (entry.state === "ABSENT" ? entry.filled_by !== null
          : typeof entry.filled_by !== "string" || entry.filled_by.trim().length === 0) return refuse("filled_by");
      declaredResidue.push({ symbol: entry.symbol, minute: entry.minute, reason: entry.reason,
        ruling: entry.ruling, state: entry.state, filled_by: entry.filled_by as string | null });
    }
  }
  const bars: PagedCandle[] = [];
  for (const [index, value] of raw.bars.entries()) {
    if (value === null || typeof value !== "object") {
      return { page: null, blocked: `${rungLabel(requested)} candle ${index} is not an object` };
    }
    const row = value as Record<string, unknown>;
    if (![row.t, row.o, row.h, row.l, row.c].every(finite) || !(row.v === null || finite(row.v))) {
      return { page: null, blocked: `${rungLabel(requested)} candle ${index} has invalid OHLCV fields` };
    }
    bars.push({ t: row.t as number, o: row.o as number, h: row.h as number, l: row.l as number,
      c: row.c as number, v: row.v as number | null });
  }
  const nextBefore = raw.next_before == null
    ? null
    : finite(raw.next_before) && Number.isSafeInteger(raw.next_before) ? raw.next_before : Number.NaN;
  if (Number.isNaN(nextBefore)) {
    return { page: null, blocked: `${rungLabel(requested)} candles served an invalid next_before cursor` };
  }
  const hasMore = raw.has_more === true;
  if (hasMore && nextBefore === null) {
    return { page: null, blocked: `${rungLabel(requested)} candles claim has_more without next_before` };
  }
  return { page: {
    bars, root: raw.root as string, servedKey: rungKey(requested), declaredResidue,
    sessionVerdict: "session_verdict" in raw ? raw.session_verdict as string | null : null,
    venueState: "venue_state" in raw ? raw.venue_state as VenueStateLike | null : null,
    nextOpen: "next_open" in raw ? raw.next_open as string | null : null,
    dataState: "data_state" in raw ? raw.data_state as string | null : null,
    sessionWindow: readSessionWindow(raw.session_window),
    ageS: finite(raw.age_s) ? raw.age_s : null,
    budgetS: finite(raw.budget_s) ? raw.budget_s : null,
    hasMore, nextBefore,
  }, blocked: null };
}

/** Empty rows are an error unless the candles door itself served the CLOSED state. */
export function candlePagePriceError(page: CandlePage): string | null {
  return page.bars.length === 0 && page.sessionVerdict !== "CLOSED"
    ? "the candles route served no bars for this window"
    : null;
}

/** Once candles have answered, their verdict replaces (rather than merely outranks) old fallbacks. */
export function preferredClosedVenueState(
  candlePageRead: boolean,
  candleVenueState: VenueStateLike | null,
  ...fallbacks: Array<VenueStateLike | null | undefined>
): VenueStateLike | null {
  if (candlePageRead) {
    return candleVenueState?.verdict === "CLOSED" ? candleVenueState : null;
  }
  return fallbacks.find((state) => state?.verdict === "CLOSED") ?? null;
}

/** Accept curve-history bars only when the response proves it served the requested interval. */
export function readVwapCoveragePage(
  body: unknown,
  requested: Rung,
  expectedFrom: number,
  expectedTo: number,
  expectedRoot = "ES",
  allowEmpty = false,
): CandlePageRead {
  const read = readCandlePage(body, requested, expectedRoot);
  if (read.page === null) return read;
  const window = body !== null && typeof body === "object"
    ? (body as Record<string, unknown>).window
    : null;
  const servedClosed = read.page.sessionWindow ?? readEchoWindow(window);
  const closedWindowEcho = (
    read.page.sessionVerdict === "CLOSED" &&
    read.page.venueState?.verdict === "CLOSED" &&
    servedClosed !== null &&
    servedClosed.from <= expectedFrom &&
    expectedTo <= servedClosed.to
  );
  const requestWindowEcho = (
    read.page.sessionVerdict !== "CLOSED" &&
    window !== null && typeof window === "object" &&
    (window as Record<string, unknown>).from === expectedFrom &&
    (window as Record<string, unknown>).to === expectedTo
  );
  const exact = closedWindowEcho || requestWindowEcho;
  if (!exact) {
    if (read.page.sessionVerdict === "CLOSED" && read.page.venueState?.verdict === "CLOSED") {
      const servedFrom = servedClosed?.from ?? "unknown";
      const servedTo = servedClosed?.to ?? "unknown";
      return { page: null, blocked: `candles range served ${servedFrom}..${servedTo} which does not cover ${expectedFrom}..${expectedTo}; refusing unproven VWAP coverage` };
    }
    return { page: null, blocked: `candles range has no exact window echo for ${expectedFrom}..${expectedTo}; refusing unproven VWAP coverage` };
  }
  const raw = body as Record<string, unknown>;
  if (raw.window_truncated === true) {
    return { page: null, blocked: `candles range ${expectedFrom}..${expectedTo} was truncated; refusing partial VWAP coverage` };
  }
  if (raw.limit_capped === true) {
    return { page: null, blocked: `candles range ${expectedFrom}..${expectedTo} was capped; refusing partial VWAP coverage` };
  }
  const empty = raw.range_empty === true || read.page.bars.length === 0;
  if (empty && (!allowEmpty || read.page.sessionVerdict === "CLOSED")) {
    return { page: null, blocked: `candles range ${expectedFrom}..${expectedTo} was empty; retention coverage is unproven` };
  }
  return read;
}

export function mergeCandlePages(older: readonly PagedCandle[], current: readonly PagedCandle[]): PagedCandle[] {
  const byTime = new Map<number, PagedCandle>();
  for (const candle of older) byTime.set(candle.t, candle);
  for (const candle of current) byTime.set(candle.t, candle);
  return [...byTime.values()].sort((a, b) => a.t - b.t);
}

/**
 * ⛔ ORDER 76 / R1310 — THE LEFT-EDGE PAN NOW FIRES AT NATIVE 1m, AND IT PAGES THROUGH THE
 * INTERVAL DOOR, NEVER A COUNT CURSOR.
 *
 * This read `canPageDeepCandles` — a >=3m predicate — so at the native rung the chart NEVER ASKED
 * for an older page at all. That, not the door, is why the owner could not scroll 1m into history:
 * MEASURED at the pooler, the existing from/to door's own predicate returns 390 bars for
 * 2025-06-02 13:30-20:00Z, a full RTH session. ⇒ ***THE DEPTH WAS ALREADY SERVED; THE CHART WAS
 * NOT REQUESTING IT.*** `alignedCandleHistoryUrl` already routes 1m to from/to and >=3m to
 * `before=`, so widening the trigger is the whole cure and the URL shape is untouched.
 *
 * ⚠ AND IT IS `canPageAlignedCandles`, NOT `canPageDeepCandles`, DELIBERATELY. Widening the DEEP
 * predicate would hand native 1m a COUNT cursor, and R1310 ruled against exactly that: a count
 * page and a CVD page drift apart across a spine gap, so the two panes would disagree about which
 * minutes exist — worse than a hole. The interval door serves the same wall-clock window the CVD
 * doors serve, which is the property that keeps the panes honest across a gap.
 */
export function shouldLoadOlderCandles(
  window: { from: number; to: number } | null,
  oldestLoadedMs: number | null,
  rung: Rung,
): boolean {
  if (window === null || oldestLoadedMs === null || !canPageAlignedCandles(rung)) return false;
  return window.from <= oldestLoadedMs + LEFT_EDGE_PREFETCH_BARS * rungSpanMs(rung);
}
